'use client';
/**
 * SegmentTabs — 세그먼트 탭 (FINAL_DESIGN_E §2 · sand 바탕 · 선택 = 네이비 채움 + 흰 글자).
 *
 * 두 모드
 *   ① 탭 모드(panels 있음 / onValueChange 있음): 실제 <button role="tab"> · tablist · aria-selected ·
 *      aria-controls · 로빙 tabindex · ←/→/Home/End 키보드(WAI-ARIA APG 「자동 활성」).
 *      panels 를 주면 «모든 패널을 SSR 로 렌더» 하고 비활성 패널은 hidden 속성으로 감춘다 —
 *      봇·사람 모두 같은 HTML(§0-5), 탭 전환은 네트워크 없이 즉시.
 *   ② 링크 모드(items 에 href): 라우트 이동 탭(블로그 카테고리 등). role=tab 이 아니라
 *      <nav> 안의 <a aria-current="page"> 다 — 탭 ARIA 를 링크에 붙이면 스크린리더가 거짓말을 한다.
 *      활성 판정은 호출부가 value 로 준다(이 컴포넌트는 pathname 을 읽지 않는다).
 *
 * 'use client' 는 ①의 상태·키보드 때문이다. 서버 컴포넌트에서 import 해도 된다 —
 * 단 서버에서 함수(onValueChange)는 넘길 수 없다. 서버 페이지는 panels 를 넘긴다.
 *
 * 375: 가로 스크롤(탭 줄 안에서만), 탭 높이 40.
 */
import Link from 'next/link';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface SegmentItem {
  id: string;
  label: ReactNode;
  /** 링크 모드. 하나라도 href 가 있으면 전부 링크로 렌더한다. */
  href?: string;
}

export interface SegmentTabsProps {
  items: SegmentItem[];
  /** 탭 목록의 접근 가능한 이름(필수 — 예: "종목 정렬 기준"). */
  label: string;
  /** 제어 모드 값. 링크 모드에서는 활성 항목 id. */
  value?: string;
  /** 비제어 초기값. 기본 첫 항목. */
  defaultValue?: string;
  /** 클라이언트 호출부 전용 콜백. */
  onValueChange?: (id: string) => void;
  /** 탭 패널(서버에서 렌더한 노드). 키 = item.id. */
  panels?: Record<string, ReactNode>;
  /** id 접두(기본 useId). 같은 페이지에 여러 개면 지정 권장 — SSR/CSR 결정성. */
  idBase?: string;
  className?: string;
}

export default function SegmentTabs({
  items, label, value, defaultValue, onValueChange, panels, idBase, className,
}: SegmentTabsProps) {
  const autoId = useId();
  const base = idBase ?? `seg${autoId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [inner, setInner] = useState<string>(defaultValue ?? items[0]?.id ?? '');
  const active = value ?? inner;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const cls = ['e-seg', className].filter(Boolean).join(' ');

  if (items.some((it) => it.href)) {
    return (
      <nav aria-label={label}>
        <div className={cls}>
          {items.map((it) => (
            <Link
              key={it.id}
              href={it.href ?? '#'}
              className="e-seg__tab"
              aria-current={it.id === active ? 'page' : undefined}
            >
              {it.label}
            </Link>
          ))}
        </div>
      </nav>
    );
  }

  const select = (id: string) => {
    if (value === undefined) setInner(id);
    onValueChange?.(id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const n = items.length;
    let next = -1;
    if (e.key === 'ArrowRight') next = (i + 1) % n;
    else if (e.key === 'ArrowLeft') next = (i - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next < 0) return;
    e.preventDefault();
    refs.current[next]?.focus();
    select(items[next].id);
  };

  return (
    <>
      <div className={cls} role="tablist" aria-label={label}>
        {items.map((it, i) => {
          const selected = it.id === active;
          return (
            <button
              key={it.id}
              ref={(el) => { refs.current[i] = el; }}
              type="button"
              role="tab"
              id={`${base}-tab-${it.id}`}
              aria-selected={selected}
              aria-controls={panels ? `${base}-panel-${it.id}` : undefined}
              tabIndex={selected ? 0 : -1}
              className="e-seg__tab"
              onClick={() => select(it.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {it.label}
            </button>
          );
        })}
      </div>
      {panels
        ? items.map((it) => (
            <div
              key={it.id}
              role="tabpanel"
              id={`${base}-panel-${it.id}`}
              aria-labelledby={`${base}-tab-${it.id}`}
              hidden={it.id !== active}
              tabIndex={0}
            >
              {panels[it.id] ?? null}
            </div>
          ))
        : null}
    </>
  );
}
